import { useEffect, useState } from 'react'

import { toast } from 'sonner'

import { MorphLabel } from '@/components/MorphLabel'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { confirmOpAction, previewOpAction, type OpActionResult } from '@/lib/opAction'

function partialText(title: string, n: number): string {
  return `${title} partially applied · ${n} change${n === 1 ? '' : 's'}`
}

export function UserActionDialog({
  op,
  params,
  title,
  critical,
  onClose,
  onDone,
}: {
  op: string
  params: Record<string, unknown>
  title: string
  critical?: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [preview, setPreview] = useState<OpActionResult | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // A confirm already came back (failed / partial / not applied): the token is spent, so
  // Confirm is hidden and only Close remains.
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    previewOpAction(op, params)
      .then((r) => {
        if (r.status === 'awaiting_approval') {
          // Four-eyes: the request was queued, nothing applied — say so instead of
          // rendering it as "nothing to do".
          toast.info('Sent for approval — a second admin must approve it in Approvals.')
          onDone()
          onClose()
        } else if (r.status === 'failed') {
          // Ran directly (no confirm gate) and Atlassian rejected it — never a no-op.
          // The dialog shows the reason; the toast only names the outcome.
          toast.error(`${title} failed`)
          if ((r.changes?.length ?? 0) > 0) onDone()
          setPreview({ ...r, confirm_token: null })
        } else if (r.status === 'partial') {
          const n = r.changes?.length ?? 0
          toast.warning(partialText(title, n))
          onDone()
          setPreview({ ...r, confirm_token: null })
        } else if (r.status === 'success') {
          // Not gated (read-only / below-threshold): the call already ran. Report what
          // it did rather than silently closing as a no-op.
          const n = r.changes?.length ?? 0
          if (n > 0) {
            toast.success(`${title} applied · ${n} change${n === 1 ? '' : 's'}`)
          } else {
            toast.info('Nothing to do — already in the desired state.')
          }
          onDone()
          onClose()
        } else {
          setPreview(r)
        }
      })
      .catch((e) => setErr((e as Error).message))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function confirm() {
    if (!preview?.confirm_token) return
    setBusy(true)
    setErr(null)
    try {
      const r = await confirmOpAction(op, params, preview.confirm_token)
      if (r.atlassian_silent_failure) {
        setErr('Atlassian acknowledged but did not apply — verify manually.')
        toast.warning(`${title} not applied`)
        setSettled(true)
      } else if (r.status === 'failed') {
        // Atlassian rejected the writes: keep the dialog open and show why (once — the
        // preview body renders the warnings; the toast only names the outcome). Only
        // refresh the page if something did get applied before the failure.
        toast.error(`${title} failed`)
        if (r.changes.length > 0) onDone()
        setPreview({ ...r, confirm_token: null })
        setSettled(true)
      } else if (r.status === 'partial') {
        // Some writes applied, some rejected: refresh the page, but leave the dialog
        // open so the admin sees which ones failed (preview renders the warnings).
        toast.warning(partialText(title, r.changes.length))
        onDone()
        setPreview({ ...r, confirm_token: null })
        setSettled(true)
      } else {
        const n = r.changes.length
        toast.success(`${title} applied${n > 0 ? ` · ${n} change${n === 1 ? '' : 's'}` : ''}`)
        onDone()
        onClose()
      }
    } catch (e) {
      // Request error: the message goes in the dialog; Confirm stays for a retry.
      setErr((e as Error).message)
      toast.error(`${title} failed`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // base-ui drives this for Escape, outside-click, and the close button —
        // all map to onClose (preview/confirm path calls onClose itself).
        if (!next && !busy) onClose()
      }}
    >
      <DialogContent
        showCloseButton={false}
        // Keep the base `fixed` positioning of DialogContent: never add `relative` here.
        className="max-w-md gap-0 overflow-hidden rounded-lg border border-line-strong bg-popover p-5"
      >
        {/* critical actions carry a red rail — anchored to the fixed popup; decorative */}
        {critical && (
          <span className="pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-danger" aria-hidden />
        )}
        {/* title row */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <DialogTitle className="text-[14px] font-semibold text-foreground">
            {title}
          </DialogTitle>
          {critical && (
            <span className="rounded border border-danger px-1.5 py-px text-[11px] font-medium text-danger">
              critical
            </span>
          )}
        </div>

        {/* error */}
        {err && (
          <p className="mb-3 font-mono text-[12px] text-danger">
            {err}
          </p>
        )}

        {/* the preview itself failed — nothing to confirm, but the dialog must still close */}
        {err && !preview && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        )}

        {/* loading preview */}
        {!preview && !err && (
          <div className="space-y-1.5 py-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="skeleton rounded"
                style={{ height: 16, width: `${60 + i * 12}%` }}
              />
            ))}
          </div>
        )}

        {/* changes list */}
        {preview && (
          <>
            {preview.changes.length === 0 ? (
              <>
                {preview.status === 'failed' ? (
                  // A real failure (e.g. unresolved group/user) — surface the
                  // reason instead of pretending it was already in desired state.
                  <p className="font-mono text-[12px] text-danger">
                    {preview.warnings.length ? preview.warnings.join('; ') : 'Operation failed.'}
                  </p>
                ) : (
                  <>
                    <p className="text-[13px] text-text-2">
                      Nothing to do — already in the desired state.
                    </p>
                    {preview.warnings.length > 0 && (
                      <p className="mt-1.5 font-mono text-[12px] text-warn">
                        {preview.warnings.join('; ')}
                      </p>
                    )}
                  </>
                )}
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="ghost" onClick={onClose}>
                    Close
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">
                  Changes · {preview.changes.length}
                </div>

                <ul className="space-y-0.5 font-mono text-[12.5px]">
                  {preview.changes.map((c, i) => {
                    const isDestructive =
                      c.action === 'remove' || c.action === 'suspend' || c.action === 'disable'
                    return (
                      <li key={i} className="flex gap-2">
                        <span className={isDestructive ? 'text-danger' : 'text-ok'} aria-hidden>
                          {isDestructive ? '−' : '+'}
                        </span>
                        <span className="sr-only">{isDestructive ? 'remove' : 'add'}</span>
                        <span className="min-w-0 break-words text-foreground">{c.target}</span>
                      </li>
                    )
                  })}
                </ul>

                {/* outcome of a confirm that did not fully apply */}
                {(preview.status === 'failed' || preview.status === 'partial') && (
                  <p className={`mt-3 text-[12px] font-medium ${preview.status === 'failed' ? 'text-danger' : 'text-warn'}`}>
                    {preview.status === 'failed' ? 'Failed — Atlassian rejected the change' : 'Partially applied — some changes were rejected'}
                  </p>
                )}

                {/* warnings */}
                {preview.warnings.length > 0 && (
                  <p className={`mt-2 font-mono text-[12px] ${preview.status === 'failed' ? 'text-danger' : 'text-warn'}`}>
                    ⚠ {preview.warnings.join('; ')}
                  </p>
                )}

                {/* blast-radius */}
                {preview.blast_radius?.summary && (
                  <div
                    className={`mt-3 rounded-md border px-2.5 py-1.5 font-mono text-[12px] ${
                      (preview.blast_radius.change_count ?? 0) >= 100 ? 'border-danger text-danger' : 'border-warn text-warn'
                    }`}
                  >
                    Impact: {preview.blast_radius.summary}
                  </div>
                )}

                {/* actions — once the outcome is final there is nothing left to confirm */}
                {settled || preview.status === 'failed' || preview.status === 'partial' ? (
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="ghost" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                ) : (
                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    variant={critical ? 'destructive' : 'default'}
                    onClick={confirm}
                    disabled={busy || !preview.confirm_token}
                  >
                    <MorphLabel>
                      {busy ? 'Applying…' : critical ? 'Confirm (critical)' : 'Confirm'}
                    </MorphLabel>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>
                    Cancel
                  </Button>
                </div>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
